import {
  Component,
  Event,
  EventEmitter,
  getAssetPath,
  h,
  Host,
  JSX,
  Prop,
  State,
} from "@stencil/core";
import { BodyType, Series } from "@phn/entities/content";
import { getConfig, isMobile } from "@phn/utility/helper";
import {
  ClickElementType,
  ClickElementTypes,
  EventAction,
  EventActions,
} from "@phn/utility/constants";
import { TDataLayer } from "@porsche-data-layer/events/projects/navi/all/types";
import { getModelsRoutingKey } from "@phn/entities/routing-key";
import { sendDataLayerEvent } from "@phn/services/analytics/data-layer-event-sender";
import analyticsManager from "@phn/services/analytics/analytics-manager";
import { navStateStore } from "@phn/state/nav-state-store";
import { tagColor } from "../model-tags/model-tags";

export enum AspectRatioClassName {
  STANDARD = "standard",
  SUV = "suv",
  PLACEHOLDER = "placeholder",
}

export type Level = "l2" | "l3";

@Component({
  tag: "phn-model-tile",
  styleUrl: "model-tile.scss",
  scoped: true,
})
export class ModelTile {
  @Prop() breakpoint!: number;

  @Prop() modelSeries: Series | BodyType;

  @Prop() level = "l2" as Level;

  @Prop() srcSet: string;

  @Prop() link = "";

  @State() tagColor: tagColor = "dark";

  @State() isFocused = false;

  @State() isHovered = false;

  @Event() imageLoad: EventEmitter<string>;

  @Event() imageLoadError: EventEmitter<string>;

  @Prop() datalayerEventAction: EventAction = EventActions.NAVIGATION_CLICK;

  @Prop() customDatalayerProperties: TDataLayer = {};

  componentWillLoad() {
    isMobile(this.breakpoint)
      ? (this.tagColor = "dark")
      : (this.tagColor = "light");
  }

  imageLoadHandler(id: string) {
    this.imageLoad.emit(id);
  }

  imageLoadErrorHandler(id: string) {
    this.imageLoadError.emit(id);
    this.srcSet = this.getFallbackSrcSet();
  }

  private sendDataLayerEvent(
    clickElementType: ClickElementType,
    elementId: string,
  ): void {
    sendDataLayerEvent({
      analyticsId: this.modelSeries.id,
      text: this.modelSeries.name,
      elementId,
      clickElementType,
      link: this.link,
      customDatalayerProperties: {
        ...this.customDatalayerProperties,
        car: {
          ...(this.customDatalayerProperties.car ?? {}),
          engineType: analyticsManager.getEngineTypes(this.modelSeries),
        },
      },
      datalayerEventAction: this.datalayerEventAction,
    });
  }

  private handleFocusChange(focusIn: boolean) {
    this.isFocused = focusIn;
    focusIn ? this.setTagColor("dark") : this.setTagColor("light");
  }

  private handleHover(hovered: boolean) {
    this.isHovered = hovered;
    this.setTagColor(hovered || this.isFocused ? "dark" : "light");
  }

  private getAspectRatioClassName(): AspectRatioClassName {
    const id = this.modelSeries.id.toLowerCase();

    if (this.modelSeries.image.includes("placeholder")) {
      return AspectRatioClassName.PLACEHOLDER;
    }

    if (id.includes("cayenne") || id.includes("macan")) {
      return AspectRatioClassName.SUV;
    }

    return AspectRatioClassName.STANDARD;
  }

  private getFallbackSrcSet(): string {
    const assetPath = getAssetPath(
      `${getConfig(navStateStore.state.env).ASSETS_URL}/model-assets/all`,
    );

    return `${assetPath}/placeholder.webp, ${assetPath}/placeholder@2x.webp 2x`;
  }

  private setTagColor(color: tagColor) {
    if (isMobile(this.breakpoint)) {
      return;
    }

    this.tagColor = color;
  }

  render(): JSX.Element {
    return (
      <Host class={`model-tile-level-${this.level}`}>
        <button
          onMouseEnter={() => this.handleHover(true)}
          onMouseLeave={() => this.handleHover(false)}
          onFocusin={() => this.handleFocusChange(true)}
          onFocusout={() => this.handleFocusChange(false)}
          onClick={() => {
            if (this.datalayerEventAction === EventActions.MODEL_RANGE_CLICK) {
              this.sendDataLayerEvent(
                ClickElementTypes.Button,
                getModelsRoutingKey(this.modelSeries.id),
              );
            }
          }}
          class="model-area-button"
        >
          <div class="model-content">
            <phn-p-heading size="medium" tag="h3" dir="auto">
              {this.modelSeries.name}
            </phn-p-heading>
            {/* eslint-disable-next-line no-prototype-builtins */}
            {this.modelSeries.hasOwnProperty("modelsAmount") ? (
              <phn-p-text color="contrast-medium">
                {(this.modelSeries as BodyType).modelsAmount}
              </phn-p-text>
            ) : null}

            <div
              class={`model-image-container
          model-image-container--${this.getAspectRatioClassName()}`}
            >
              <div class="model-image-container-ratio-content">
                <img
                  class="model-image"
                  srcSet={this.srcSet}
                  alt=""
                  onLoad={() => this.imageLoadHandler(this.modelSeries.id)}
                  onError={() =>
                    this.imageLoadErrorHandler(this.modelSeries.id)
                  }
                />
              </div>
            </div>

            <div class="model-tags">
              <phn-model-tags
                data-test-id="model-tags"
                tagColor={this.tagColor}
                modelTags={this.modelSeries.tags}
              />
            </div>
          </div>
        </button>
      </Host>
    );
  }
}
